import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { Box } from 'reflexbox';
import axios from 'axios';

import {
  Divider,
  Text,
  Textarea,
} from 'rebass';

import SInput from 'shared/simcinput';

import { derror } from 'frontend/util';
import {
  If,
  Loader,
  ErrorText,
  ToggleView,
} from 'frontend/components';

import CopyToClipboard from 'frontend/components/CopyToClipboard';

class SimcInput extends React.Component {
  static propTypes = {
    simId: PropTypes.string.isRequired,
    report: PropTypes.shape({
      simbot: PropTypes.object,
    }).isRequired,
  };

  state = {
    loading: true,
    input: null,
    inputIsLarge: false,
    error: null,
  };

  async componentDidMount() {
    this.fetchInput();
  }

  componentWillUnmount() {
    if (this.getInputSource) {
      this.getInputSource.cancel('componentWillUnmount');
    }
  }

  getInputUrl = () => {
    const simId = this.getSimId();
    return `/reports/${simId}/input.txt`;
  };

  getSimId = () => {
    return this.props.simId
      || _.get(this, 'props.report.simbot.parentSimId')
      || _.get(this, 'props.report.simbot.simId');
  };

  fetchInput = async () => {
    this.setState({
      loading: true,
      error: null,
    });

    try {
      this.getInputSource = axios.CancelToken.source();
      const headResponse = await axios.head(this.getInputUrl(), {
        cancelToken: this.getInputSource.token,
      });
      const contentLength = parseInt(
        headResponse.headers['x-goog-stored-content-length'] || headResponse.headers['content-length'],
        10,
      );

      if (contentLength > 100000) {
        this.setState({
          loading: false,
          inputIsLarge: true,
        });
      } else {
        const response = await axios.get(this.getInputUrl(), { cancelToken: this.getInputSource.token });
        this.setState({
          loading: false,
          input: response.data,
          error: null,
        });
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        derror(error);
        this.setState({
          loading: false,
          error,
        });
      }
    }
  };

  render() {
    const { report } = this.props;

    const meta = report?.simbot?.meta;
    const rawFormData = meta?.rawFormData;

    if (report.simbot && report.simbot.input) {
      let addonInput = rawFormData?.text;
      if (report.simbot.simType === 'optimize') {
        addonInput = SInput.stringify(rawFormData?.optimize?.data);
      }
      const customApl = rawFormData?.apl;

      const isExpertMode = meta?.expertMode;
      const expertOptions = _.pick(
        rawFormData,
        ['expertHeader', 'expertPreActors', 'expertRaidActors', 'expertPostActors', 'expertFooter'],
      );

      return (
        <Box mt={1}>
          <ToggleView open={false} label="Raw Input">
            <Text small>
              This text is intended to be used with a downloaded version of the
              {' '}
              <a
                href="http://www.simulationcraft.org/download.html"
                target="_blank"
                rel="noreferrer noopener"
              >
                SimulationCraft program
              </a>
            </Text>
            <Text small>
              Results may differ - Raidbots uses a custom build that may differ from the SimulationCraft nightly build
            </Text>
            <Text small bold>Note! On Raidbots, this input will only work in the Advanced tool!</Text>
            <Box sm={12} lg={12} mt={1}>
              <If
                condition={this.state.error}
                then={() => (
                  <ErrorText>
                    Unable to fetch input
                  </ErrorText>
                )}
                else={() => (
                  <Loader
                    loading={this.state.loading}
                    loaded={() => (
                      <If
                        condition={this.state.inputIsLarge}
                        then={() => (
                          <Box>
                            <Text>
                              <strong>
                                <a
                                  href={this.getInputUrl()}
                                  // eslint-disable-next-line react/jsx-no-target-blank
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  Download input.txt
                                </a>
                              </strong>
                              {' '}
                              (Too large to display in-page)
                            </Text>
                          </Box>
                        )}
                        else={() => (
                          <>
                            <Textarea
                              label=""
                              name="rawInput"
                              rows={10}
                              value={this.state.input}
                              backgroundColor="rgba(255,255,255,.45)"
                              readOnly
                              mb={0}
                              style={{ fontSize: 13 }}
                            />
                            <CopyToClipboard value={this.state.input} />
                          </>
                        )}
                      />
                    )}
                  />
                )}
              />
            </Box>
          </ToggleView>
          <If
            condition={addonInput}
            then={() => {
              return (
                <>
                  <Divider my={2} />
                  <Box mt={2}>
                    <ToggleView
                      label="Original Addon Input"
                      defaultOpen={false}
                    >
                      <Box sm={12} lg={12} mt={1}>
                        <Text small>Original input from SimulationCraft addon</Text>
                        <Textarea
                          label="Simc Addon Input"
                          name="addonInput"
                          cols={200}
                          rows={10}
                          value={addonInput}
                          backgroundColor="rgba(255,255,255,.45)"
                          readOnly
                          mb={0}
                          style={{ fontSize: 13 }}
                        />
                        <CopyToClipboard value={addonInput} />
                      </Box>
                    </ToggleView>
                  </Box>
                </>
              );
            }}
          />
          <If
            condition={customApl}
            then={() => (
              <>
                <Divider my={2} />
                <ToggleView
                  label="Custom APL"
                  defaultOpen={false}
                >
                  <Box sm={12} lg={12} mt={1}>
                    <Text small>User-provided custom APL and SimC Options</Text>
                    <Textarea
                      label="Custom APL and SimC Options"
                      name="addonInput"
                      cols={200}
                      rows={10}
                      value={customApl}
                      backgroundColor="rgba(255,255,255,.45)"
                      readOnly
                      mb={0}
                      style={{ fontSize: 13 }}
                    />
                    <CopyToClipboard value={customApl} />
                  </Box>
                </ToggleView>
              </>
            )}
          />
          <If
            condition={isExpertMode}
            then={() => (
              <>
                <Divider my={2} />
                <ToggleView
                  label="Expert Mode Options"
                  defaultOpen={false}
                >
                  <Box sm={12} lg={12} mt={1}>
                    {_.map(expertOptions, (value, name) => (
                      <Box key={name}>
                        <Textarea
                          label={_.startCase(name)}
                          name={name}
                          cols={200}
                          rows={5}
                          value={value}
                          backgroundColor="rgba(255,255,255,.45)"
                          readOnly
                          mb={0}
                          style={{ fontSize: 13 }}
                        />
                        <CopyToClipboard value={value} />
                      </Box>
                    ))}
                  </Box>
                </ToggleView>
              </>
            )}
          />
        </Box>
      );
    }

    return null;
  }
}

export default SimcInput;
